import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';
import { isItemExpired } from '../../../utils/proposalCalculations';

import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// Utils
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import rebateServices from '../../../services/rebateServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Styles
import { button } from '../../../static/theme-styles';

// Simple ID generator
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalRebatesSection = ({ proposal, id, refresh }) => {
  const [rebates, setRebates] = useState(proposal?.contractRebates || []);
  const [rebateCatalog, setRebateCatalog] = useState([]);
  const [addingRebate, setAddingRebate] = useState(false);
  const [selectedRebateId, setSelectedRebateId] = useState('');
  const isSigned = !!(proposal?.signatureId || proposal?.signature);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  useEffect(() => {
    setRebates(proposal?.contractRebates || []);
  }, [proposal?.contractRebates]);

  // Load rebate catalog
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await rebateServices.getAll();
        if (response.status === 200) {
          setRebateCatalog(response.data);
        }
      } catch (error) {
        console.error('Failed to load rebate catalog:', error);
      }
    };
    loadCatalog();
  }, []);

  const handleAddRebate = async () => {
    if (!selectedRebateId) {
      toast.error('Please select a rebate from the catalog');
      return;
    }

    const selectedRebate = rebateCatalog.find(r => r.id === Number(selectedRebateId));
    if (!selectedRebate) {
      toast.error('Selected rebate not found');
      return;
    }

    // Check for duplicates
    if (rebates.some(r => r.catalogId === selectedRebate.id)) {
      toast.error('This rebate has already been added');
      return;
    }

    const newRebate = {
      uuid: generateId(),
      catalogId: selectedRebate.id,
      name: selectedRebate.name,
      amount: parseFloat(selectedRebate.amount),
      expires: moment().endOf('year').format('YYYY-MM-DD'),
      isSelected: true,
    };

    const updatedRebates = [...rebates, newRebate];
    await handleSave(updatedRebates);
    setAddingRebate(false);
    setSelectedRebateId('');
  };

  const handleToggleSelected = async (uuid, isSelected) => {
    const updatedRebates = rebates.map(r => 
      r.uuid === uuid ? { ...r, isSelected } : r
    );
    await handleSave(updatedRebates);
  };

  const handleDelete = async (uuid, name) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${name}"`),
    })
      .then(async () => {
        const updatedRebates = rebates.filter(r => r.uuid !== uuid);
        await handleSave(updatedRebates);
        toast.success('Rebate removed');
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleSave = async (updatedRebates) => {
    try {
      const response = await proposalsServices.update(id, {
        contractRebates: updatedRebates,
      });
      if (response.status === 200) {
        setRebates(updatedRebates);
        if (refresh) refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const updatedRebates = arrayMoveImmutable(rebates, oldIndex, newIndex);
    handleSave(updatedRebates);
  };

  const totalRebates = rebates
    .filter((r) => r.isSelected)
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return (
    <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }} elevation={2}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        mb: 2 
      }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Contract Rebates</Typography>
        <Typography variant={isMobile ? 'body2' : 'subtitle1'} color="primary" sx={{ fontWeight: 600 }}>
          Total: ${totalRebates.toFixed(2)}
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: isMobile ? 'unset' : 400 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}></TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right" sx={{ width: isMobile ? 70 : 120 }}>Amount</TableCell>
              <TableCell align="center" sx={{ width: isMobile ? 50 : 80 }}>Active</TableCell>
              <TableCell align="center" sx={{ width: isMobile ? 40 : 60 }}></TableCell>
            </TableRow>
          </TableHead>
          <SortableTableBody useDragHandle onSortEnd={onSortEnd} helperClass="sortable-row-helper">
            {rebates.map((rebate, index) => (
              <SortableTableRow 
                key={rebate.uuid || index} 
                index={index}
                sx={isItemExpired(rebate, isSigned) ? { backgroundColor: '#fff5f5', opacity: 0.75 } : undefined}
              >
                <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DragHandle />
                    {isItemExpired(rebate, isSigned) && (
                      <Chip label="EXPIRED" size="small" color="error" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>{rebate.name}</Typography>
                  {rebate.description && !isMobile && (
                    <Typography variant="caption" color="textSecondary">
                      {rebate.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '0.8rem' : 'inherit',
                      color: rebate.isSelected ? 'success.main' : 'text.disabled'
                    }}
                  >
                    ${parseFloat(rebate.amount || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined }}>
                  <Checkbox
                    checked={rebate.isSelected}
                    onChange={(e) => handleToggleSelected(rebate.uuid, e.target.checked)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDelete(rebate.uuid, rebate.name)}
                    color="error"
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </SortableTableRow>
            ))}
          </SortableTableBody>
        </Table>
      </TableContainer>

      {rebates.length === 0 && !addingRebate && (
        <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
          No rebates added. Click "Add Rebate" to select from catalog.
        </Typography>
      )}

      {addingRebate && (
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          gap: 2, 
          alignItems: isMobile ? 'stretch' : 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <FormControl size="small" sx={{ minWidth: isMobile ? 'unset' : 300, width: isMobile ? '100%' : 'auto' }}>
            <InputLabel>Select Rebate from Catalog</InputLabel>
            <Select
              value={selectedRebateId}
              label="Select Rebate from Catalog"
              onChange={(e) => setSelectedRebateId(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 300 }
                }
              }}
            >
              <MenuItem value="" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>
                <em>-- Select --</em>
              </MenuItem>
              {rebateCatalog.map((rebate) => (
                <MenuItem 
                  key={rebate.id} 
                  value={rebate.id}
                  disabled={rebates.some(r => r.catalogId === rebate.id)}
                  sx={{ 
                    whiteSpace: 'normal', 
                    fontSize: isMobile ? '0.8rem' : 'inherit',
                    py: isMobile ? 1 : 0.75
                  }}
                >
                  {rebate.name} — ${parseFloat(rebate.amount).toFixed(2)}
                  {rebates.some(r => r.catalogId === rebate.id) && ' (already added)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
            <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton size="small" color="primary" onClick={handleAddRebate}>
                <SaveIcon />
              </IconButton>
            </Grid>
            <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton size="small" onClick={() => { setAddingRebate(false); setSelectedRebateId(''); }}>
                <CancelIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      )}

      {!addingRebate && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddingRebate(true)}
          sx={{ mt: 2 }}
        >
          Add Rebate
        </Button>
      )}
    </Paper>
  );
};

export default ProposalRebatesSection;
